'use client';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * @primitive Toast (Sonner-backed)
 * ROLE: status (default) or alert (danger/error) | ARIA: role wired via Sonner's toastOptions
 * KEYBOARD: Escape clears all toasts; individual toasts accessible via Tab; action buttons Tab-navigable
 * FOCUS: toasts do not steal focus on open; keyboard users reach them via Tab
 * LIVE-REGION: success/info/warning → role="status" (polite); error/danger → role="alert" (assertive)
 * PRM: animation disabled; opacity instant
 * FORCED-COLORS: toast border = ButtonText; action button border = ButtonText
 * RTL: position flips via dir attribute
 * RSC: CLIENT
 * WAI-APG: https://www.w3.org/WAI/ARIA/apg/patterns/alert/
 * SUBSTRATE: sonner
 *
 * Usage:
 *   1. Mount <ToastProvider /> once in AppShell (or layout root).
 *   2. Call the imperative API anywhere:
 *      toast.success("Saved")
 *      toast.error("Couldn't save", { action: { label: "Retry", onClick: retry } })
 *      toast.warning("Session expiring soon")
 *      toast.info("New feature available")
 *      toast("Plain message")
 */
import { Toaster, toast as sonnerToast } from 'sonner';
import styles from './Toast.module.css';
/**
 * Mount once in the app shell. Configures the Sonner Toaster with Lean tokens and a11y roles.
 */
export function ToastProvider({ position = 'bottom-right', duration = 5000, visibleToasts = 5, dir = 'auto', }) {
    return (_jsx(Toaster, { position: position, duration: duration, visibleToasts: visibleToasts, dir: dir, className: styles.toaster, toastOptions: {
            classNames: {
                toast: styles.toast,
                title: styles.toastTitle,
                description: styles.toastDescription,
                actionButton: styles.actionButton,
                cancelButton: styles.cancelButton,
                closeButton: styles.closeButton,
                error: styles.toastError,
                success: styles.toastSuccess,
                warning: styles.toastWarning,
                info: styles.toastInfo,
            },
        } }));
}
export const toast = {
    success: sonnerToast.success,
    error: sonnerToast.error,
    warning: sonnerToast.warning,
    info: sonnerToast.info,
    message: sonnerToast,
    dismiss: sonnerToast.dismiss,
    loading: sonnerToast.loading,
    promise: sonnerToast.promise,
};
//# sourceMappingURL=Toast.js.map